import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { fullPhoneNumberRegex } from "@/utils/regex";
import { useLoader } from "@/context/LoaderContext";
import {
  ConfirmationResult,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  signInWithCustomToken,
} from "firebase/auth";
import { auth } from "@/firebase.config";
import { useCountdown } from "@/hooks/useCountdown";
import { queryClient } from "@/services/react-query";
import { resetTokenRefresh } from "@/services/api";
import { getPhoneNumberAvailability, updateUserInfo } from "@/services/users";
import { sendWhatsappCode, validateWhatsappCode, firebaseSignup } from "@/services/auth";
import { getBlacklist } from "@/services/blacklist";
import {
  getFirebasePhoneAuthToastMessage,
  isFirebasePhoneAuthCaptchaError,
} from "@/utils/firebasePhoneAuthErrors";
import {
  clearPendingPhoneValidatedSyncFlag,
  setPendingPhoneValidatedSyncFlag,
} from "@/utils/phoneValidatedSync";
import { SignInLocationState } from "../SignUp/hooks/useSignup";

interface CustomWindow extends Window {
  confirmationResult?: ConfirmationResult | null;
  recaptchaVerifier?: RecaptchaVerifier;
  recaptchaWidgetId?: number;
}

declare const window: CustomWindow;

// Comes from Google reCAPTCHA V3 script included in the HTML file
declare const grecaptcha: any;

const DISABLE_RECAPTCHA_FOR_TESTING =
  String(import.meta.env.VITE_DISABLE_RECAPTCHA_FOR_TESTING).toLowerCase() ===
  "true";

export const useSignin = () => {
  const loader = useLoader();
  const navigate = useNavigate();
  const location = useLocation();
  const signInEntry = (location.state as SignInLocationState | null) ?? null;
  const prefilledPhone = signInEntry?.prefilledPhone ?? "";
  const { value: smsTimerValue, restart: restartTimer } = useCountdown();

  const [phoneNumber, setPhoneNumber] = useState(prefilledPhone);

  const [isLoading, setIsLoading] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [otp, setOtp] = useState("");

  const [currentStep, setCurrentStep] = useState(1);

  const [showSubscriptionsEndedDialog, setShowSubscriptionsEndedDialog] =
    useState(false);
  const [showNoAccountDialog, setShowNoAccountDialog] = useState(false);
  const [isOpenBlacklistedModal, setIsOpenBlacklistedModal] = useState(false);
  const toggleSubscriptionsEndedDialog = () =>
    setShowSubscriptionsEndedDialog(!showSubscriptionsEndedDialog);

  const [isOpenChooseValidation, setIsOpenChooseValidation] = useState(false);
  /** While sending SMS/WhatsApp code: blocks double-submit and drives Dialog loading (Loader Backdrop sits below modal z-index). */
  const [validationType, setValidationType] = useState<"sms" | "whatsapp">();

  const rawPhoneNumber = useMemo(() => {
    if (!phoneNumber) return "";

    return "+55" + phoneNumber.replace(/[^\d]/g, "");
  }, [phoneNumber]);

  const phoneNumberError = useMemo(() => {
    if (rawPhoneNumber?.length === 0) return "";

    const isValid = fullPhoneNumberRegex.test(rawPhoneNumber);

    if (!isValid) {
      return "Número de telefone inválido.";
    }

    return "";
  }, [rawPhoneNumber]);

  const onClickSignIn = async () => {
    try {
      setIsLoading(true);
      if (!phoneNumber) {
        return toast("Preencha todos os campos", { type: "error" });
      }

      if (phoneNumberError) {
        return;
      }

      const blacklist = await getBlacklist();
      if (blacklist.phones.includes(rawPhoneNumber)) {
        return setIsOpenBlacklistedModal(true);
      }

      const { isAvailable } = await getPhoneNumberAvailability(rawPhoneNumber);
      if (isAvailable) {
        return setShowNoAccountDialog(true);
      }

      if (currentStep === 1) {
        return setIsOpenChooseValidation(true);
      }

      if (validationType === "sms") {
        await verifyCaptcha();
      } else {
        await validateWhatsapp();
      }
    } catch (err: any) {
      console.error("Error signing-in", err);
      if (err?.response?.status === 403) {
        toggleSubscriptionsEndedDialog();
      } else {
        toast("Erro ao fazer login. Verifique seu telefone", {
          type: "error",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  /** Direct "Quero me cadastrar" keeps the phone verification intro on sign-up. */
  const goToSignUp = (options?: { fromLoginNoAccount?: boolean }) => {
    if (options?.fromLoginNoAccount) {
      setShowNoAccountDialog(false);
      navigate("/sign-up", {
        state: { skipPhoneIntro: true, prefilledPhone: phoneNumber },
      });
      return;
    }
    navigate("/sign-up");
  };

  const resetReCaptcha = () => {
    if (
      typeof grecaptcha !== "undefined" &&
      typeof window.recaptchaWidgetId !== "undefined"
    ) {
      grecaptcha.reset(window.recaptchaWidgetId);
    }
  };

  const verifyCaptcha = async () => {
    try {
      loader.show("login");

      await window.confirmationResult?.confirm(otp);

      // Fix 7: Verificar se claims existem, se não, chamar backend para setar
      const user = auth.currentUser;
      if (user) {
        const token = await user.getIdToken(true);
        // Decodificar payload do JWT para verificar claims
        const payload = JSON.parse(atob(token.split('.')[1]));

        if (!payload.appUserId) {
          // Claims não existem, chamar endpoint firebase-signup para setar
          console.log("Claims ausentes, chamando firebase-signup...");
          await firebaseSignup(rawPhoneNumber);
          // Forçar refresh do token para obter claims atualizados
          await user.getIdToken(true);
        }
      }

      // Next API call must force a fresh ID token (claims) on the axios interceptor
      resetTokenRefresh();

      setPendingPhoneValidatedSyncFlag();
      try {
        await updateUserInfo({ phoneNumberValidated: true });
        clearPendingPhoneValidatedSyncFlag();
      } catch (err) {
        console.error("Erro ao salvar verificação do celular após login", err);
      }
      await queryClient.invalidateQueries({ queryKey: ["user-info"], exact: false });
      navigate("/dashboard");
    } catch (error) {
      toast("Código inválido ou inexistente", { type: "error" });
      console.error("Código inválido ou inexistente", error);
      resetReCaptcha();
    } finally {
      loader.hide("login");
    }
  };

  const validateWhatsapp = async () => {
    try {
      loader.show("verify_code");
      const { isExpired, isValid, accessToken } = await validateWhatsappCode(
        rawPhoneNumber,
        otp
      );

      if (isExpired) {
        toast("Código expirado!", { type: "error" });
        return false;
      }

      if (!isValid) {
        toast("Código inválido ou inexistente", { type: "error" });
        return false;
      }

      await signInWithCustomToken(auth, accessToken);
      // Forçar refresh do ID token para obter claims atualizados
      await auth.currentUser?.getIdToken(true);
      resetTokenRefresh();

      setPendingPhoneValidatedSyncFlag();
      try {
        await updateUserInfo({ phoneNumberValidated: true });
        clearPendingPhoneValidatedSyncFlag();
      } catch (err) {
        console.error("Erro ao salvar verificação do celular após login (WhatsApp)", err);
      }
      await queryClient.invalidateQueries({ queryKey: ["user-info"], exact: false });
      navigate("/dashboard");
    } catch (err) {
      toast("Erro ao validar o código", { type: "error" });
      console.error("Erro ao validar o código", err);
    } finally {
      loader.hide("verify_code");
    }
  };

  const sendSignInSmsCode = async (disableNextStep = false) => {
    if (isSendingCode) return;
    setIsSendingCode(true);
    loader.show("sign_in");
    setValidationType("sms");

    if (!rawPhoneNumber.length) {
      loader.hide("sign_in");
      setIsSendingCode(false);
      return;
    }

    try {
      if (window.recaptchaVerifier && window.recaptchaWidgetId === undefined) {
        const widgetId = await window.recaptchaVerifier.render();
        window.recaptchaWidgetId = widgetId;
      }

      const verifier = window.recaptchaVerifier!;

      const sendSmsOnce = () =>
        signInWithPhoneNumber(auth, rawPhoneNumber, verifier);

      let confirmationResult: ConfirmationResult;
      try {
        confirmationResult = await sendSmsOnce();
      } catch (firstErr) {
        if (isFirebasePhoneAuthCaptchaError(firstErr)) {
          resetReCaptcha();
          await new Promise((r) => setTimeout(r, 600));
          confirmationResult = await sendSmsOnce();
        } else {
          throw firstErr;
        }
      }

      window.confirmationResult = confirmationResult;
      if (!disableNextStep) setCurrentStep(2);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn("[sendSignInSmsCode]", error);
      }
      toast(getFirebasePhoneAuthToastMessage(error), {
        type: "error",
        toastId: "phone-auth-send-error",
      });
      resetReCaptcha();
    } finally {
      loader.hide("sign_in");
      setIsSendingCode(false);
      setIsOpenChooseValidation(false);
    }
  };

  const onSendWhatsappCode = async (disableNextStep = false) => {
    if (isSendingCode) return;
    setIsSendingCode(true);
    setValidationType("whatsapp");

    try {
      loader.show("send_whatsapp_code");
      await sendWhatsappCode(rawPhoneNumber);
      if (!disableNextStep) setCurrentStep(2);
    } catch (err) {
      console.error("Erro ao enviar código", err);
      toast("Erro ao enviar código", { type: "error" });
    } finally {
      setIsOpenChooseValidation(false);
      setIsSendingCode(false);
      loader.hide("send_whatsapp_code");
    }
  };

  const onFixPhoneNumber = () => {
    setIsOpenChooseValidation(false);
    setCurrentStep(1);
    setOtp("");
  };

  const resendCode = () => {
    restartTimer();
    if (validationType === "sms") {
      sendSignInSmsCode(true);
    } else {
      onSendWhatsappCode(true);
    }
  };

  useEffect(() => {
    auth.settings.appVerificationDisabledForTesting =
      DISABLE_RECAPTCHA_FOR_TESTING;

    window.recaptchaVerifier = new RecaptchaVerifier(
      auth,
      "recaptcha-container",
      {
        size: "invisible",
        // Must not call sendSignInSmsCode here — same as sign-up: duplicates SMS and breaks token flow.
        callback: () => undefined,
      }
    );
    return () => {
      auth.settings.appVerificationDisabledForTesting = false;
      try {
        window.recaptchaVerifier?.clear();
      } catch {
        /* ignore */
      }
      window.recaptchaVerifier = undefined;
      window.recaptchaWidgetId = undefined;
    };
  }, []);

  useEffect(() => {
    resetReCaptcha();
  }, [rawPhoneNumber]);

  useEffect(() => {
    restartTimer();
  }, [currentStep]);

  return {
    currentStep,
    setCurrentStep,

    phoneNumber,
    setPhoneNumber,
    phoneNumberError,

    otp,
    setOtp,

    onClickSignIn,
    isLoading,
    goToSignUp,
    isOpenChooseValidation,
    setIsOpenChooseValidation,

    showSubscriptionsEndedDialog,
    toggleSubscriptionsEndedDialog,

    smsTimerValue,
    sendSignInSmsCode,
    onSendWhatsappCode,
    onFixPhoneNumber,

    showNoAccountDialog,
    setShowNoAccountDialog,
    isOpenBlacklistedModal,
    setIsOpenBlacklistedModal,
    resendCode,

    isSendingCode,
  };
};
